import { FormEvent, useMemo, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DialpadOutlinedIcon from "@mui/icons-material/DialpadOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";

import CustomDialog from "../../../components/Dialog/Dialog";
import HeaderTitle from "components/PageHeader/HeaderTitle";
import { formatPhoneNumber, getDigitsOnly } from "utils";
import { useStoreUsers } from "../hooks/queries/userQueries";
import { useDeleteUserMutation, useInviteUserMutation, useUpdateUserMutation } from "../hooks";
import { useAuth } from "../../auth";
import { User, UserFormData } from "../types";
import {
    ADD_USER_ROLE_OPTIONS,
    EDIT_USER_ROLE_OPTIONS,
    OWNER_USER_ROLE_OPTIONS,
    formatUserRole,
    getAssignableUserRoleOptions,
    getUserPermissionLabels,
    getUserRolePrivilegeRank,
    isRecoOfficerUser,
    isPinLoginAllowedForRole,
    normalizeUserRole,
    canAssignReconciliationManagerRole,
} from "../utils/userRoleUtils";
import AuditCodeManagement from "./AuditCodeManagement";

type UserRole = UserFormData["role"];
type LoginMethod = "phone" | "email" | "pin";
type DialogMode = "add" | "edit";

type UserFormState = {
    role: UserRole;
    name: string;
    loginMethod: LoginMethod;
    phoneNumber: string;
    email: string;
    cashierModePin: string;
    canApproveReconciliation: boolean;
};

type UserFormErrors = Partial<Record<keyof UserFormState, string>>;
type StoreOutletContext = {
    storeDetails?: {
        user_role?: string | null;
    } | null;
};

const DEFAULT_FORM_STATE: UserFormState = {
    role: "operator",
    name: "",
    loginMethod: "phone",
    phoneNumber: "",
    email: "",
    cashierModePin: "",
    canApproveReconciliation: false,
};

const fieldSx = {
    "& .MuiOutlinedInput-root": {
        minHeight: "46px",
        borderRadius: "8px",
        backgroundColor: "#FFFFFF",
        fontFamily: "Inter, sans-serif",
        fontSize: 14,
    },
    "& .MuiInputBase-input": {
        textAlign: "left !important",
    },
    "& .MuiOutlinedInput-input": {
        height: "20px !important",
        padding: "13px 14px !important",
        boxSizing: "content-box",
    },
    "& .MuiSelect-select": {
        minHeight: "20px !important",
        padding: "13px 14px !important",
        display: "flex",
        alignItems: "center",
    },
    "& .MuiInputLabel-root": {
        fontFamily: "Inter, sans-serif",
        fontSize: 14,
        transform: "translate(14px, 13px) scale(1)",
    },
    "& .MuiInputLabel-shrink": {
        transform: "translate(14px, -9px) scale(0.75)",
    },
    "& .MuiFormHelperText-root": {
        marginLeft: 0,
        fontFamily: "Inter, sans-serif",
    },
};

const leftAlignedCellSx = {
    textAlign: "left !important",
};

const rightAlignedCellSx = {
    textAlign: "right !important",
};

const fieldLabelSx = {
    display: "block",
    color: "#334155",
    fontFamily: "Inter, sans-serif",
    fontSize: 12,
    fontWeight: 800,
    mb: 0.75,
};

const fieldHelpSx = {
    color: "#64748B",
    fontFamily: "Inter, sans-serif",
    fontSize: 12,
    fontWeight: 500,
    mt: 0.5,
};

const normalizeEmail = (email?: string | null) => String(email || "").trim();

const getUserEmail = (user: User) => normalizeEmail(user.contact_email || user.email || user.email_address);

const formatPhone = (phone?: string | null) => {
    const digits = getDigitsOnly(String(phone || ""));
    return digits ? formatPhoneNumber(digits) : "-";
};

const displayValue = (value?: string | null) => {
    const normalized = String(value || "").trim();
    return normalized || "-";
};

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const getDisplayName = (user: User) => user.display_name || user.name || getUserEmail(user) || user.phone_number || "User";

const resolveLoginMethod = (user: User): LoginMethod => {
    const explicitLoginField = String((user as User & { login_field?: string }).login_field || "").toLowerCase();
    if (explicitLoginField === "email" || explicitLoginField === "phone") {
        return explicitLoginField;
    }

    const hasPhone = Boolean(getDigitsOnly(String(user.phone_number || "")));
    const hasEmail = Boolean(getUserEmail(user));
    if (user.has_pin && !hasPhone && !hasEmail) {
        return "pin";
    }

    if (hasEmail && !hasPhone) {
        return "email";
    }

    return "phone";
};

const getLoginLabel = (loginMethod: LoginMethod) => {
    if (loginMethod === "email") {
        return "Email OTP";
    }
    if (loginMethod === "pin") {
        return "Pin";
    }
    return "Phone OTP";
};

const getLoginIcon = (loginMethod: LoginMethod) => {
    if (loginMethod === "email") {
        return <EmailOutlinedIcon fontSize="small" />;
    }
    if (loginMethod === "pin") {
        return <DialpadOutlinedIcon fontSize="small" />;
    }
    return <PhoneOutlinedIcon fontSize="small" />;
};

export default function UsersList() {
    const { id } = useParams<{ id: string }>();
    const outletContext = useOutletContext<StoreOutletContext | undefined>();
    const { state: authState } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { data: storeUsersResponse, isLoading } = useStoreUsers(id || "");
    const inviteUserMutation = useInviteUserMutation();
    const updateUserMutation = useUpdateUserMutation();
    const deleteUserMutation = useDeleteUserMutation();

    const [dialogMode, setDialogMode] = useState<DialogMode>("add");
    const [userDialogOpen, setUserDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [formState, setFormState] = useState<UserFormState>(DEFAULT_FORM_STATE);
    const [formErrors, setFormErrors] = useState<UserFormErrors>({});
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState<User | null>(null);

    const storeUsers = useMemo(() => {
        const responseData = storeUsersResponse?.data;
        const users = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.data)
                ? responseData.data
                : [];

        return users
            .filter((user: User & { is_hidden?: boolean }) => !user.is_hidden)
            .sort((firstUser: User, secondUser: User) => {
                const firstRank = getUserRolePrivilegeRank(firstUser);
                const secondRank = getUserRolePrivilegeRank(secondUser);

                if (firstRank !== secondRank) {
                    return firstRank - secondRank;
                }

                return getDisplayName(firstUser).localeCompare(getDisplayName(secondUser), undefined, {
                    sensitivity: "base",
                });
            });
    }, [storeUsersResponse]);

    const isSubmitting = inviteUserMutation.isPending || updateUserMutation.isPending;
    const isEditMode = dialogMode === "edit";
    const isEditingOwner = isEditMode && normalizeUserRole(editingUser?.role) === "owner";
    const assignableRoleOptions = getAssignableUserRoleOptions(outletContext?.storeDetails?.user_role);
    const roleOptions = isEditingOwner ? OWNER_USER_ROLE_OPTIONS : isEditMode ? EDIT_USER_ROLE_OPTIONS.filter((role) => assignableRoleOptions.includes(role)) : ADD_USER_ROLE_OPTIONS.filter((role) => assignableRoleOptions.includes(role));
    const canManageRecoOfficerPermission = canAssignReconciliationManagerRole(
        outletContext?.storeDetails?.user_role,
        Boolean(authState.user?.is_super_admin)
    );
    const formRole = normalizeUserRole(formState.role);
    const showRecoOfficerPermission = canManageRecoOfficerPermission && (formRole === "manager" || formRole === "owner");
    const pinLoginDisabled = !isPinLoginAllowedForRole(formState.role);
    const dialogTitle = isEditMode ? "Edit User" : "Add User";

    const updateForm = <K extends keyof UserFormState>(field: K, value: UserFormState[K]) => {
        setFormState((current) => ({ ...current, [field]: value }));
        setFormErrors((current) => ({ ...current, [field]: undefined }));
    };

    const handleRoleChange = (event: SelectChangeEvent<UserRole>) => {
        const nextRole = event.target.value as UserRole;
        setFormState((current) => ({
            ...current,
            role: nextRole,
            canApproveReconciliation: nextRole === "manager" || nextRole === "owner" ? current.canApproveReconciliation : false,
            loginMethod: isPinLoginAllowedForRole(nextRole) ? current.loginMethod : "phone",
            cashierModePin: isPinLoginAllowedForRole(nextRole) ? current.cashierModePin : "",
        }));
        setFormErrors({});
    };

    const handleLoginMethodChange = (loginMethod: LoginMethod) => {
        if (loginMethod === "pin" && pinLoginDisabled) {
            return;
        }

        setFormState((current) => ({
            ...current,
            loginMethod,
            phoneNumber: loginMethod === "pin" ? "" : current.phoneNumber,
            email: loginMethod === "pin" ? "" : current.email,
            cashierModePin: loginMethod === "pin" ? current.cashierModePin : "",
        }));
        setFormErrors({});
    };

    const openAddDialog = () => {
        setDialogMode("add");
        setEditingUser(null);
        setFormState(DEFAULT_FORM_STATE);
        setFormErrors({});
        setUserDialogOpen(true);
    };

    const openEditDialog = (user: User) => {
        setDialogMode("edit");
        setEditingUser(user);
        setFormState({
            role: isRecoOfficerUser(user) ? "manager" : user.role as UserRole,
            name: getDisplayName(user),
            loginMethod: resolveLoginMethod(user),
            phoneNumber: getDigitsOnly(String(user.phone_number || "")),
            email: getUserEmail(user),
            cashierModePin: "",
            canApproveReconciliation: isRecoOfficerUser(user),
        });
        setFormErrors({});
        setUserDialogOpen(true);
    };

    const closeUserDialog = () => {
        if (isSubmitting) {
            return;
        }
        setUserDialogOpen(false);
        setEditingUser(null);
        setFormErrors({});
        setFormState(DEFAULT_FORM_STATE);
    };

    const validateForm = () => {
        const nextErrors: UserFormErrors = {};
        const trimmedName = formState.name.trim();
        const trimmedEmail = formState.email.trim();
        const phoneDigits = getDigitsOnly(formState.phoneNumber);
        const pinDigits = getDigitsOnly(formState.cashierModePin);

        if (!trimmedName) {
            nextErrors.name = "Display name is required.";
        } else if (trimmedName.length < 2) {
            nextErrors.name = "Display name must be at least 2 characters.";
        }

        if (!formState.role) {
            nextErrors.role = "Role is required.";
        }

        if (isEditMode) {
            if (normalizeUserRole(editingUser?.role) !== "owner" && normalizeUserRole(formState.role) === "owner") {
                nextErrors.role = "Owner role cannot be assigned here.";
            }
            if (normalizeUserRole(editingUser?.role) === "owner" && normalizeUserRole(formState.role) !== "owner") {
                nextErrors.role = "Owner role cannot be changed here.";
            }
            if (trimmedEmail && !isValidEmail(trimmedEmail)) {
                nextErrors.email = "Enter a valid email address.";
            }
        } else if (formState.loginMethod === "phone") {
            if (!phoneDigits) {
                nextErrors.phoneNumber = "Phone number is required.";
            } else if (phoneDigits.length !== 10) {
                nextErrors.phoneNumber = "Phone number must be 10 digits.";
            }
            if (trimmedEmail && !isValidEmail(trimmedEmail)) {
                nextErrors.email = "Enter a valid email address.";
            }
        } else if (formState.loginMethod === "email") {
            if (!trimmedEmail) {
                nextErrors.email = "Email address is required.";
            } else if (!isValidEmail(trimmedEmail)) {
                nextErrors.email = "Enter a valid email address.";
            }
            if (phoneDigits && phoneDigits.length !== 10) {
                nextErrors.phoneNumber = "Phone number must be 10 digits.";
            }
        } else {
            if (pinLoginDisabled) {
                nextErrors.loginMethod = "PIN login is only available for operators.";
            }
            if (!pinDigits) {
                nextErrors.cashierModePin = "PIN is required.";
            } else if (pinDigits.length !== 6) {
                nextErrors.cashierModePin = "PIN must be 6 digits.";
            }
        }

        setFormErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmitUser = async (event: FormEvent) => {
        event.preventDefault();

        if (!id || isSubmitting || !validateForm()) {
            return;
        }

        const trimmedName = formState.name.trim();
        const trimmedEmail = formState.email.trim();
        const phoneDigits = getDigitsOnly(formState.phoneNumber);
        const pinDigits = getDigitsOnly(formState.cashierModePin);

        try {
            if (isEditMode && editingUser) {
                const requestData: {
                    role: UserRole;
                    display_name: string;
                    contact_email: string | null;
                    can_approve_reconciliation?: boolean;
                } = {
                    role: formState.role,
                    display_name: trimmedName,
                    contact_email: trimmedEmail || null,
                };
                if (canManageRecoOfficerPermission) {
                    requestData.can_approve_reconciliation = ["manager", "owner"].includes(normalizeUserRole(formState.role)) && formState.canApproveReconciliation;
                }
                await updateUserMutation.mutateAsync({
                    storeId: id,
                    userId: editingUser.user_id,
                    userData: requestData,
                });
            } else {
                const requestData: {
                    name: string;
                    role: UserRole;
                    login_field?: "phone" | "email";
                    phone_number?: string | null;
                    email?: string;
                    cashier_mode_pin?: string;
                    can_approve_reconciliation?: boolean;
                } = {
                    name: trimmedName,
                    role: formState.role,
                };
                if (canManageRecoOfficerPermission) {
                    requestData.can_approve_reconciliation = ["manager", "owner"].includes(normalizeUserRole(formState.role)) && formState.canApproveReconciliation;
                }

                if (formState.loginMethod === "pin") {
                    requestData.cashier_mode_pin = pinDigits;
                } else if (formState.loginMethod === "email") {
                    requestData.login_field = "email";
                    requestData.email = trimmedEmail;
                    requestData.phone_number = phoneDigits || null;
                } else {
                    requestData.login_field = "phone";
                    requestData.phone_number = phoneDigits;
                    if (trimmedEmail) {
                        requestData.email = trimmedEmail;
                    }
                }

                const response = await inviteUserMutation.mutateAsync({ storeId: id, userData: requestData });
                if (!response.success) {
                    return;
                }
            }

            closeUserDialog();
        } catch {
            // Mutation hooks surface the user-facing toast.
        }
    };

    const onRemoveUser = (user: User) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!id || !userToDelete) {
            return;
        }

        try {
            await deleteUserMutation.mutateAsync({ storeId: id, userId: userToDelete.user_id });
            setDeleteDialogOpen(false);
            setUserToDelete(null);
        } catch (error) {
            console.error("Error deleting user:", error);
        }
    };

    const handlePhoneChange = (value: string) => {
        updateForm("phoneNumber", getDigitsOnly(value).slice(0, 10));
    };

    const handlePinChange = (value: string) => {
        updateForm("cashierModePin", getDigitsOnly(value).slice(0, 6));
    };

    const renderLoginMethod = (loginMethod: LoginMethod) => (
        <Box
            sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                color: loginMethod === "pin" ? "#7C3AED" : "#2563EB",
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1.2,
            }}
        >
            {getLoginIcon(loginMethod)}
            <span>{getLoginLabel(loginMethod)}</span>
        </Box>
    );

    const renderRoleWithPermissions = (user: User) => {
        const permissions = getUserPermissionLabels(user);

        return (
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.75, mt: 0.35 }}>
                <Typography component="span" sx={{ color: "#64748B", fontSize: 12.5, fontWeight: 600, lineHeight: 1.25 }}>
                    {formatUserRole(user.role)}
                </Typography>
                {permissions.map((permission) => (
                    <Typography
                        key={permission}
                        component="span"
                        sx={{
                            border: "1px solid #B7D7C8",
                            borderRadius: "999px",
                            color: "#047857",
                            fontSize: 10.5,
                            fontWeight: 800,
                            letterSpacing: "0.04em",
                            lineHeight: 1,
                            px: 0.75,
                            py: 0.35,
                            textTransform: "uppercase",
                        }}
                    >
                        {permission}
                    </Typography>
                ))}
            </Box>
        );
    };

    const renderRecoOfficerPermission = () => {
        if (!showRecoOfficerPermission) {
            return null;
        }

        return (
            <Box
                sx={{
                    border: "1px solid #D9E6F2",
                    borderRadius: "10px",
                    px: 1.5,
                    py: 1.25,
                    backgroundColor: "#F8FBFE",
                }}
            >
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={formState.canApproveReconciliation}
                            onChange={(event) => updateForm("canApproveReconciliation", event.target.checked)}
                            sx={{
                                color: "#436FA4",
                                "&.Mui-checked": {
                                    color: "#0F766E",
                                },
                            }}
                        />
                    }
                    label={
                        <Box>
                            <Typography sx={{ color: "#1F2A44", fontSize: 13, fontWeight: 700, lineHeight: 1.3 }}>
                                Can reconcile and approve mismatched audits
                            </Typography>
                            <Typography sx={{ color: "#64748B", fontSize: 12, fontWeight: 500, lineHeight: 1.35, mt: 0.25 }}>
                                Adds the Reco Officer permission and allows approval actions.
                            </Typography>
                        </Box>
                    }
                    sx={{
                        alignItems: "flex-start",
                        m: 0,
                        "& .MuiFormControlLabel-label": {
                            fontFamily: "Inter, sans-serif",
                        },
                    }}
                />
            </Box>
        );
    };

    const renderDialogFields = () => {
        if (isEditMode) {
            const loginMethod = formState.loginMethod;

            return (
                <Stack spacing={1.75}>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1.5,
                        }}
                    >
                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                Display Name
                            </Typography>
                            <TextField
                                autoFocus
                                placeholder="Name"
                                value={formState.name}
                                error={Boolean(formErrors.name)}
                                helperText={formErrors.name}
                                onChange={(event) => updateForm("name", event.target.value)}
                                fullWidth
                                sx={fieldSx}
                            />
                        </Box>
                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                Role
                            </Typography>
                            <FormControl fullWidth error={Boolean(formErrors.role)} sx={fieldSx}>
                                <Select
                                    value={formState.role}
                                    disabled={isEditingOwner}
                                    onChange={handleRoleChange}
                                >
                                    {roleOptions.map((role) => (
                                        <MenuItem key={role} value={role}>
                                            {formatUserRole(role)}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
                            </FormControl>
                        </Box>
                    </Box>
                    {renderRecoOfficerPermission()}
                    <Box
                        sx={{
                            border: "1px solid #E2E8F0",
                            borderRadius: "10px",
                            px: 1.75,
                            py: 1.5,
                            backgroundColor: "#F8FAFC",
                        }}
                    >
                        <Typography sx={{ color: "#436FA4", fontSize: 11, fontWeight: 800, mb: 0.75, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                            Login
                        </Typography>
                        {renderLoginMethod(loginMethod)}
                    </Box>
                    <Box>
                        <Typography component="label" sx={fieldLabelSx}>
                            Email
                        </Typography>
                        <TextField
                            placeholder="Optional contact email"
                            value={formState.email}
                            error={Boolean(formErrors.email)}
                            helperText={formErrors.email || "Optional contact email."}
                            onChange={(event) => updateForm("email", event.target.value)}
                            fullWidth
                            sx={fieldSx}
                        />
                    </Box>
                </Stack>
            );
        }

        return (
            <Stack spacing={1.75}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 1.5,
                    }}
                >
                    <Box>
                        <Typography component="label" sx={fieldLabelSx}>
                            Display Name
                        </Typography>
                        <TextField
                            autoFocus
                            placeholder="New user"
                            value={formState.name}
                            error={Boolean(formErrors.name)}
                            helperText={formErrors.name}
                            onChange={(event) => updateForm("name", event.target.value)}
                            fullWidth
                            sx={fieldSx}
                        />
                    </Box>
                    <Box>
                        <Typography component="label" sx={fieldLabelSx}>
                            Role
                        </Typography>
                        <FormControl fullWidth error={Boolean(formErrors.role)} sx={fieldSx}>
                            <Select
                                value={formState.role}
                                onChange={handleRoleChange}
                            >
                                {roleOptions.map((role) => (
                                    <MenuItem key={role} value={role}>
                                        {formatUserRole(role)}
                                    </MenuItem>
                                ))}
                            </Select>
                            {formErrors.role && <FormHelperText>{formErrors.role}</FormHelperText>}
                        </FormControl>
                    </Box>
                </Box>
                {renderRecoOfficerPermission()}

                <Box>
                    <Typography component="div" sx={fieldLabelSx}>
                        Login Type
                    </Typography>
                    <Box
                        role="group"
                        aria-label="Login type"
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
                            gap: 1.25,
                        }}
                    >
                        {(["phone", "email", "pin"] as LoginMethod[]).map((loginMethod) => {
                            const selected = formState.loginMethod === loginMethod;
                            const disabled = loginMethod === "pin" && pinLoginDisabled;
                            const description = loginMethod === "phone"
                                ? "Send OTP to a phone number."
                                : loginMethod === "email"
                                    ? "Send OTP to an email address."
                                    : "Operator cashier login only.";
                            return (
                                <Button
                                    key={loginMethod}
                                    variant="outlined"
                                    disabled={disabled}
                                    onClick={() => handleLoginMethodChange(loginMethod)}
                                    sx={{
                                        alignItems: "flex-start",
                                        borderRadius: "12px",
                                        borderColor: selected ? "rgba(67, 111, 164, 0.42)" : "#CBD5E1",
                                        backgroundColor: selected ? "#EAF4FD" : "#FFFFFF",
                                        boxShadow: selected ? "inset 4px 0 0 #436FA4" : "none",
                                        color: "#1F2A44",
                                        display: "flex",
                                        flexDirection: "column",
                                        fontFamily: "Inter, sans-serif",
                                        minHeight: 72,
                                        px: 1.5,
                                        py: 1.25,
                                        textTransform: "none",
                                        textAlign: "left",
                                        "&:hover": {
                                            borderColor: selected ? "rgba(67, 111, 164, 0.58)" : "#94A3B8",
                                            backgroundColor: selected ? "#EAF4FD" : "#F8FAFC",
                                        },
                                        "&.Mui-disabled": {
                                            backgroundColor: "#F7F8FB",
                                            borderColor: "#E2E8F0",
                                            color: "#A3AED0",
                                        },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 13, fontWeight: 800, lineHeight: 1.2 }}>
                                        {getLoginIcon(loginMethod)}
                                        {getLoginLabel(loginMethod).replace(" OTP", "")}
                                    </Box>
                                    <Typography component="span" sx={{ color: disabled ? "#A3AED0" : "#64748B", fontSize: 12, fontWeight: 500, lineHeight: 1.35, mt: 0.45 }}>
                                        {description}
                                    </Typography>
                                </Button>
                            );
                        })}
                    </Box>
                    <Typography sx={fieldHelpSx}>
                        {formState.loginMethod === "phone"
                            ? "Phone login requires a phone number."
                            : formState.loginMethod === "email"
                                ? "Email login requires an email address."
                                : "PIN login is only available for operator users."}
                    </Typography>
                    {formErrors.loginMethod && (
                        <Typography sx={{ mt: 0.75, color: "#DC2626", fontSize: 12 }}>
                            {formErrors.loginMethod}
                        </Typography>
                    )}
                </Box>

                {formState.loginMethod === "phone" && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1.5,
                        }}
                    >
                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                Phone Number
                            </Typography>
                            <TextField
                                placeholder="2095550134"
                                value={formState.phoneNumber ? formatPhoneNumber(formState.phoneNumber) : ""}
                                error={Boolean(formErrors.phoneNumber)}
                                helperText={formErrors.phoneNumber || "Required for phone login."}
                                onChange={(event) => handlePhoneChange(event.target.value)}
                                inputProps={{ inputMode: "numeric" }}
                                fullWidth
                                sx={fieldSx}
                            />
                        </Box>
                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                Email
                            </Typography>
                            <TextField
                                placeholder="Optional contact email"
                                value={formState.email}
                                error={Boolean(formErrors.email)}
                                helperText={formErrors.email || "Optional contact."}
                                onChange={(event) => updateForm("email", event.target.value)}
                                fullWidth
                                sx={fieldSx}
                            />
                        </Box>
                    </Box>
                )}

                {formState.loginMethod === "email" && (
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                            gap: 1.5,
                        }}
                    >
                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                Email
                            </Typography>
                            <TextField
                                placeholder="user@example.com"
                                value={formState.email}
                                error={Boolean(formErrors.email)}
                                helperText={formErrors.email || "Required for email login."}
                                onChange={(event) => updateForm("email", event.target.value)}
                                fullWidth
                                sx={fieldSx}
                            />
                        </Box>
                        <Box>
                            <Typography component="label" sx={fieldLabelSx}>
                                Phone Number
                            </Typography>
                            <TextField
                                placeholder="Optional contact phone"
                                value={formState.phoneNumber ? formatPhoneNumber(formState.phoneNumber) : ""}
                                error={Boolean(formErrors.phoneNumber)}
                                helperText={formErrors.phoneNumber || "Optional contact."}
                                onChange={(event) => handlePhoneChange(event.target.value)}
                                inputProps={{ inputMode: "numeric" }}
                                fullWidth
                                sx={fieldSx}
                            />
                        </Box>
                    </Box>
                )}

                {formState.loginMethod === "pin" && (
                    <Box>
                        <Typography component="label" sx={fieldLabelSx}>
                            Cashier PIN
                        </Typography>
                        <TextField
                            placeholder="Six digits"
                            value={formState.cashierModePin}
                            type="password"
                            error={Boolean(formErrors.cashierModePin)}
                            helperText={formErrors.cashierModePin || "Six digits. Allowed only for operator users."}
                            onChange={(event) => handlePinChange(event.target.value)}
                            inputProps={{ inputMode: "numeric", maxLength: 6 }}
                            fullWidth
                            sx={fieldSx}
                        />
                    </Box>
                )}
            </Stack>
        );
    };

    return (
        <Box
            className="user-list-container"
            sx={{
                fontFamily: "Inter, sans-serif",
                color: "#1E293B",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 1.5,
                    flexDirection: "row",
                    width: "100%",
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <HeaderTitle
                        title="Manage Users"
                        sx={{
                            "& h1": {
                                fontSize: { xs: "20px", sm: "24px" },
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                            },
                        }}
                    />
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openAddDialog}
                    sx={{
                        flexShrink: 0,
                        minWidth: { xs: 72, sm: 88 },
                        borderRadius: "8px",
                        backgroundColor: "#2563EB",
                        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.12)",
                        color: "#FFFFFF",
                        fontFamily: "Inter, sans-serif",
                        fontSize: { xs: 13, sm: 14 },
                        fontWeight: 700,
                        px: { xs: 1.4, sm: 1.75 },
                        py: { xs: 0.7, sm: 0.85 },
                        textTransform: "none",
                        "&:hover": {
                            backgroundColor: "#1D4ED8",
                            boxShadow: "0 2px 4px rgba(15, 23, 42, 0.16)",
                        },
                    }}
                >
                    Add
                </Button>
            </Box>

            <TableContainer
                sx={{
                    border: "1px solid #E2E8F0",
                    borderRadius: "12px",
                    backgroundColor: "#FFFFFF",
                    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
                    overflowX: "auto",
                    overflowY: "hidden",
                    WebkitOverflowScrolling: "touch",
                }}
            >
                <Table
                    sx={{
                        minWidth: 760,
                        tableLayout: "fixed",
                        "& .users-table__cell": {
                            px: "12px !important",
                            textAlign: "left !important",
                        },
                        "& .users-table__cell--actions": {
                            textAlign: "right !important",
                        },
                        "& .users-table__empty": {
                            textAlign: "center !important",
                        },
                    }}
                >
                    <colgroup>
                        <col style={{ width: "30%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "28%" }} />
                        <col style={{ width: "12%" }} />
                    </colgroup>
                    <TableHead>
                        <TableRow
                            sx={{
                                backgroundColor: "#F5F7FB",
                                "& th": {
                                    borderBottom: "1px solid #E2E8F0",
                                    color: "#64748B",
                                    fontFamily: "Inter, sans-serif",
                                    fontSize: 12,
                                    fontWeight: 800,
                                    letterSpacing: "0.04em",
                                    lineHeight: 1,
                                    py: 1.5,
                                    textTransform: "uppercase",
                                    whiteSpace: "nowrap",
                                },
                            }}
                        >
                            <TableCell className="users-table__cell" align="left" sx={leftAlignedCellSx}>User</TableCell>
                            <TableCell className="users-table__cell" align="left" sx={leftAlignedCellSx}>Login</TableCell>
                            <TableCell className="users-table__cell" align="left" sx={leftAlignedCellSx}>Phone</TableCell>
                            <TableCell className="users-table__cell" align="left" sx={leftAlignedCellSx}>Email</TableCell>
                            <TableCell className="users-table__cell users-table__cell--actions" align="right" sx={rightAlignedCellSx}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={5} className="users-table__empty" sx={{ py: 6 }}>
                                    <Box sx={{ width: "100%", textAlign: "center" }}>
                                        <CircularProgress size={24} />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : storeUsers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="users-table__empty" sx={{ py: 6 }}>
                                    <Box sx={{ width: "100%", textAlign: "center" }}>
                                        <Typography sx={{ color: "#475569", fontSize: 14, fontWeight: 700 }}>
                                            No users yet
                                        </Typography>
                                        <Typography sx={{ color: "#94A3B8", fontSize: 13, mt: 0.5 }}>
                                            Add the first manager or operator for this store.
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            storeUsers.map((user: User) => {
                                const loginMethod = resolveLoginMethod(user);
                                return (
                                    <TableRow
                                        key={user.id || user.user_id}
                                        hover
                                        sx={{
                                            "& td": {
                                                borderBottom: "1px solid #E2E8F0",
                                                color: "#1E293B",
                                                fontFamily: "Inter, sans-serif",
                                                fontSize: 14,
                                                py: 1.55,
                                                verticalAlign: "middle",
                                            },
                                            "&:last-of-type td": {
                                                borderBottom: 0,
                                            },
                                            "&:hover": {
                                                backgroundColor: "#F8FAFC",
                                            },
                                        }}
                                    >
                                        <TableCell className="users-table__cell" align="left" sx={leftAlignedCellSx}>
                                            <Typography sx={{ color: "#1F2A44", fontSize: 14, fontWeight: 800, lineHeight: 1.35 }}>
                                                {getDisplayName(user)}
                                            </Typography>
                                            {renderRoleWithPermissions(user)}
                                        </TableCell>
                                        <TableCell className="users-table__cell" align="left" sx={leftAlignedCellSx}>{renderLoginMethod(loginMethod)}</TableCell>
                                        <TableCell className="users-table__cell" align="left" sx={{ ...leftAlignedCellSx, color: user.phone_number ? "#334155" : "#94A3B8" }}>
                                            {loginMethod === "pin" ? "-" : formatPhone(user.phone_number)}
                                        </TableCell>
                                        <TableCell className="users-table__cell" align="left" sx={{ ...leftAlignedCellSx, color: getUserEmail(user) ? "#334155" : "#94A3B8" }}>
                                            {loginMethod === "pin" ? "-" : displayValue(getUserEmail(user))}
                                        </TableCell>
                                        <TableCell className="users-table__cell users-table__cell--actions" align="right" sx={rightAlignedCellSx}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: { xs: "column", sm: "row" },
                                                    alignItems: { xs: "flex-start", sm: "center" },
                                                    justifyContent: { xs: "flex-start", sm: "flex-end" },
                                                    gap: { xs: 0.5, sm: 1.5 },
                                                    width: "100%",
                                                }}
                                            >
                                                <Button
                                                    type="button"
                                                    onClick={() => openEditDialog(user)}
                                                    sx={{
                                                        color: "#436FA4",
                                                        fontFamily: "Inter, sans-serif",
                                                        fontSize: 13,
                                                        fontWeight: 800,
                                                        minWidth: 0,
                                                        lineHeight: 1.1,
                                                        p: 0,
                                                        textTransform: "none",
                                                        "&:hover": {
                                                            backgroundColor: "transparent",
                                                            color: "#2F5E91",
                                                            textDecoration: "underline",
                                                        },
                                                    }}
                                                >
                                                    Edit
                                                </Button>
                                                <Button
                                                    type="button"
                                                    onClick={() => onRemoveUser(user)}
                                                    sx={{
                                                        color: "#DC2626",
                                                        fontFamily: "Inter, sans-serif",
                                                        fontSize: 13,
                                                        fontWeight: 800,
                                                        minWidth: 0,
                                                        lineHeight: 1.1,
                                                        p: 0,
                                                        textTransform: "none",
                                                        "&:hover": {
                                                            backgroundColor: "transparent",
                                                            color: "#B91C1C",
                                                            textDecoration: "underline",
                                                        },
                                                    }}
                                                >
                                                    Remove
                                                </Button>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {id && (
                <Box sx={{ mt: 2 }}>
                    <AuditCodeManagement storeId={id} />
                </Box>
            )}

            <Dialog
                open={userDialogOpen}
                onClose={closeUserDialog}
                scroll="paper"
                fullScreen={isMobile}
                fullWidth
                maxWidth={false}
                PaperProps={{
                    sx: {
                        width: isMobile ? "100%" : "min(560px, calc(100% - 24px))",
                        maxHeight: isMobile ? "100dvh" : "calc(100dvh - 24px)",
                        borderRadius: "18px",
                        border: "1px solid #CBD5E1",
                        boxShadow: "0 25px 60px rgba(15, 23, 42, 0.18)",
                        display: "flex",
                        flexDirection: "column",
                        ...(isMobile && {
                            borderRadius: 0,
                            border: 0,
                            boxShadow: "none",
                        }),
                        overflow: "hidden",
                    },
                }}
                BackdropProps={{
                    sx: {
                        backgroundColor: "rgba(15, 23, 42, 0.28)",
                    },
                }}
            >
                <Box
                    component="form"
                    onSubmit={handleSubmitUser}
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        flex: 1,
                    }}
                >
                    <DialogTitle
                        sx={{
                            alignItems: "flex-start",
                            background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
                            borderBottom: "1px solid #E2E8F0",
                            color: "#1F2A44",
                            display: "flex",
                            fontFamily: "Inter, sans-serif",
                            justifyContent: "space-between",
                            gap: 1.75,
                            px: 2.75,
                            py: 2.25,
                        }}
                    >
                        <Box>
                            <Typography sx={{ color: "#1F2A44", fontSize: 20, fontWeight: 800, lineHeight: 1.25 }}>
                                {dialogTitle}
                            </Typography>
                            <Typography sx={{ color: "#64748B", fontSize: 13, fontWeight: 500, mt: 0.5 }}>
                                {isEditMode ? "Update this store user's display details." : "Choose the role, then choose how this user logs in."}
                            </Typography>
                        </Box>
                        <IconButton
                            aria-label="Close"
                            onClick={closeUserDialog}
                            disabled={isSubmitting}
                            sx={{
                                border: "1px solid #CBD5E1",
                                borderRadius: "50%",
                                color: "#64748B",
                                height: 34,
                                width: 34,
                                backgroundColor: "#FFFFFF",
                                "&:hover": {
                                    backgroundColor: "#F8FAFC",
                                },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            px: { xs: 2, sm: 2.75 },
                            py: { xs: 1.75, sm: 2.25 },
                            overflowY: "auto",
                            minHeight: 0,
                            flex: 1,
                            WebkitOverflowScrolling: "touch",
                            overscrollBehavior: "contain",
                        }}
                    >
                        {renderDialogFields()}
                    </DialogContent>
                    <DialogActions
                        sx={{
                            px: { xs: 2, sm: 2.75 },
                            pb: { xs: 2, sm: 2.5 },
                            pt: 0.5,
                            gap: 1.25,
                            flexShrink: 0,
                        }}
                    >
                        <Button
                            onClick={closeUserDialog}
                            disabled={isSubmitting}
                            sx={{
                                borderRadius: "8px",
                                border: "1px solid #CBD5E1",
                                color: "#475569",
                                fontFamily: "Inter, sans-serif",
                                fontSize: 13,
                                fontWeight: 800,
                                minWidth: 82,
                                px: 1.75,
                                py: 0.95,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "#F8FAFC",
                                    borderColor: "#94A3B8",
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={isSubmitting}
                            sx={{
                                borderRadius: "8px",
                                backgroundColor: "#436FA4",
                                boxShadow: "none",
                                fontFamily: "Inter, sans-serif",
                                fontSize: 13,
                                fontWeight: 800,
                                minWidth: 92,
                                px: 1.85,
                                py: 1,
                                textTransform: "none",
                                "&:hover": {
                                    backgroundColor: "#2F5E91",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            {isSubmitting ? "Saving..." : isEditMode ? "Save" : "Add user"}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <CustomDialog
                open={deleteDialogOpen}
                handleClose={() => {
                    setDeleteDialogOpen(false);
                    setUserToDelete(null);
                }}
                title="Delete User"
                buttonTxt="Delete"
                cancelBtn={true}
                cancelBtnTxt="Cancel"
                btnColor="#d32f2f"
                handleSubmit={handleConfirmDelete}
                disabled={deleteUserMutation.isPending}
                isLoading={deleteUserMutation.isPending}
                width="400px"
            >
                <Box sx={{ textAlign: "center" }}>
                    <p style={{ margin: "0 0 16px 0", fontSize: "16px", color: "#333" }}>
                        Are you sure you want to remove <strong>{userToDelete ? getDisplayName(userToDelete) : "this user"}</strong> from this store?
                    </p>
                    <p style={{ margin: "0", fontSize: "14px", color: "#666" }}>
                        This action cannot be undone.
                    </p>
                </Box>
            </CustomDialog>
        </Box>
    );
}
